'use client';

import { Fragment } from 'react';
import Link from 'next/link';
import styles from './NorthStarMetric.module.scss';
import { Target } from 'lucide-react';
import { gaCta } from '@/src/helpers/gtag';
import { useAOS } from '@/src/lib/hooks/useAOS';

interface NorthStarMetricProps {
  eyebrow?: string;
  title?: string;
  highlightedTitle?: string;
  description?: string;
  ctaLabel?: string;
  ctaHref?: string;
  tilt?: 'left' | 'right';
  id?: string;
}

const renderTitle = (text?: string) => {
  if (!text) {
    return null;
  }
  const parts = text.split('*');
  return parts.map((part, i) =>
    i % 2 === 1 ? <em key={i}>{part}</em> : <Fragment key={i}>{part}</Fragment>
  );
};

export function NorthStarMetric({
  eyebrow = 'NORTH STAR METRIC',
  title = 'Vous définissez le résultat.',
  highlightedTitle = '*Nous gérons le chemin.*',
  description = 'Une seule métrique guide la roadmap. Pas de vanity metrics : chaque sprint doit rapprocher l’objectif final.',
  ctaLabel,
  ctaHref,
  tilt = 'right',
  id,
}: NorthStarMetricProps) {
  useAOS();

  const tiltClass = tilt === 'left' ? styles.tiltLeft : styles.tiltRight;

  return (
    <section id={id} className={`${styles.root} ${tiltClass}`}>
      <div className={styles.inner}>
        <div className={styles.card} data-aos="fade-up" data-aos-duration="800">
          <div className={styles.visual} aria-hidden="true">
            <div className={styles.ring} />
            <div className={styles.ring} />
            <div className={styles.core}>
              <Target size={28} strokeWidth={1.8} />
            </div>
          </div>

          <div className={styles.content}>
            {eyebrow && <span className={styles.eyebrow}>{eyebrow}</span>}
            <h2 className={styles.title}>
              {renderTitle(title)}{' '}
              <span className={styles.highlight}>{renderTitle(highlightedTitle)}</span>
            </h2>
            {description && <p className={styles.description}>{description}</p>}
            {ctaLabel && ctaHref && (
              <Link
                href={ctaHref}
                className={styles.cta}
                onClick={() => gaCta(ctaLabel, 'north_star_cta', ctaHref)}
              >
                {ctaLabel}
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
